import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, CalendarClock, Settings2 } from "lucide-react";

const TYPE_LABEL = { sick: "Sick Leave", annual: "Annual Leave", personal: "Personal Leave", unpaid: "Unpaid Leave",
  other: "Other", unavailable: "Unavailable", planned_time_off: "Planned Time Off" };
const STATUS_TONE = { submitted: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-500" };

export default function Leave() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [wtype, setWtype] = useState("");
  const [balances, setBalances] = useState([]);
  const [settings, setSettings] = useState({ contractor_requires_approval: true });
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [showManage, setShowManage] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (wtype) p.set("worker_type", wtype);
    api.get(`/leave?${p.toString()}`).then((r) => setItems(r.data));
  }, [status, wtype]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/leave/balances").then((r) => setBalances(r.data));
    api.get("/settings/leave").then((r) => setSettings(r.data));
  }, []);

  const approve = (id) => api.post(`/leave/${id}/approve`).then(() => { toast.success("Approved"); load(); });
  const cancel = (id) => api.post(`/leave/${id}/cancel`).then(() => { toast.success("Cancelled"); load(); });
  const doDecline = () => {
    if (!declineReason.trim()) { toast.error("Reason required"); return; }
    api.post(`/leave/${declineFor}/decline`, { reason: declineReason }).then(() => {
      toast.success("Declined"); setDeclineFor(null); setDeclineReason(""); load();
    });
  };
  const saveEntitlement = (wid, days) => api.put(`/employees/${wid}/leave-entitlement`, { annual_entitlement_days: parseFloat(days) || 0 })
    .then(() => { toast.success("Updated"); api.get("/leave/balances").then((r) => setBalances(r.data)); });
  const toggleSetting = (val) => api.put("/settings/leave", { contractor_requires_approval: val }).then((r) => { setSettings(r.data); toast.success("Saved"); });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-leave-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Leave &amp; Availability</h1>
          <p className="text-slate-500 mt-1">Review leave requests and staff unavailability</p>
        </div>
        <Button variant="outline" onClick={() => setShowManage(true)} data-testid="leave-manage-btn"><Settings2 className="w-4 h-4 mr-2" />Manage</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select data-testid="leave-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
          <option value="">All Statuses</option>
          <option value="submitted">Awaiting Approval</option>
          <option value="approved">Approved</option>
          <option value="declined">Declined</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select data-testid="leave-filter-type" value={wtype} onChange={(e) => setWtype(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
          <option value="">All Workers</option>
          <option value="employee">Employees</option>
          <option value="contractor">Contractors</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-semibold">Worker</th>
                <th className="text-left px-3 py-3 font-semibold">Type</th>
                <th className="text-left px-3 py-3 font-semibold">Request</th>
                <th className="text-left px-3 py-3 font-semibold">Dates</th>
                <th className="text-left px-3 py-3 font-semibold">Notes</th>
                <th className="text-center px-3 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-10">No requests.</td></tr>}
              {items.map((r) => (
                <tr key={r.id} data-testid={`leave-row-${r.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-navy">{r.worker_name}</td>
                  <td className="px-3 py-3 capitalize text-slate-500">{r.worker_type}</td>
                  <td className="px-3 py-3">{TYPE_LABEL[r.request_type] || r.request_type}</td>
                  <td className="px-3 py-3 text-slate-600">{formatDate(r.start_date)}{r.end_date !== r.start_date ? ` – ${formatDate(r.end_date)}` : ""}{!r.full_day ? ` (${r.start_time}–${r.end_time})` : ""}</td>
                  <td className="px-3 py-3 text-slate-500 max-w-[200px] truncate">{r.notes || "—"}{r.status === "declined" && r.decline_reason ? ` · Declined: ${r.decline_reason}` : ""}</td>
                  <td className="text-center px-3 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_TONE[r.status]}`}>{r.status}</span></td>
                  <td className="text-right px-4 py-3">
                    {r.status === "submitted" && (
                      <div className="flex gap-1.5 justify-end">
                        <button onClick={() => approve(r.id)} data-testid={`leave-approve-${r.id}`} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setDeclineFor(r.id)} data-testid={`leave-decline-${r.id}`} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                    {r.status === "approved" && <button onClick={() => cancel(r.id)} className="text-xs text-slate-400 hover:text-red-600">Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!declineFor} onOpenChange={(o) => !o && setDeclineFor(null)}>
        <DialogContent data-testid="leave-decline-dialog">
          <DialogHeader><DialogTitle>Decline Leave Request</DialogTitle><DialogDescription>A reason is required and will be shared with the worker.</DialogDescription></DialogHeader>
          <Textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="Reason for declining" rows={3} data-testid="leave-decline-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineFor(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doDecline} data-testid="leave-decline-confirm">Decline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showManage} onOpenChange={setShowManage}>
        <DialogContent data-testid="leave-manage-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Leave Settings & Entitlements</DialogTitle><DialogDescription>Set annual leave entitlements and contractor approval rules.</DialogDescription></DialogHeader>
          <label className="flex items-center justify-between gap-2 text-sm text-slate-700 border border-slate-100 rounded-xl p-3">
            <span>Contractor unavailability requires approval</span>
            <input type="checkbox" checked={settings.contractor_requires_approval} onChange={(e) => toggleSetting(e.target.checked)} data-testid="leave-contractor-approval-toggle" />
          </label>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />Annual Leave Entitlements (days/year)</p>
            {balances.map((b) => (
              <div key={b.worker_id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-navy">{b.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{b.annual_remaining_days} left</span>
                  <Input type="number" defaultValue={b.annual_entitlement_days} className="w-20 h-8" data-testid={`entitlement-${b.worker_id}`}
                    onBlur={(e) => saveEntitlement(b.worker_id, e.target.value)} />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={() => setShowManage(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
